import {
  Component,
  DestroyRef,
  ElementRef,
  EventEmitter,
  Input,
  NgZone,
  OnInit,
  Output,
  ViewChild
} from '@angular/core';
import { DragFileUploadDirective } from '../../../../../../../shared/directives/drag-file-upload.directive';
import { IconComponent } from '../../../../../../../shared/components/ui/icon/icon.component';
import { TranslatePipe } from '../../../../../../../shared/pipes/translate/translate.pipe';
import { MediaApiService } from '../../../../../../../api/bizzmine/media/media-api.service';
import {
  asyncScheduler,
  BehaviorSubject,
  debounceTime,
  filter,
  lastValueFrom,
  map,
  Observable,
  of,
  shareReplay,
  switchMap,
  tap
} from 'rxjs';
import { AsyncPipe, DatePipe, NgClass, NgStyle } from '@angular/common';
import { ExtensionIconComponent } from '../../../../../../../shared/components/ui/icon/extension-icon/extension-icon.component';
import { PopoverModule } from '@progress/kendo-angular-tooltip';
import { PopupModule } from '@progress/kendo-angular-popup';
import { userSettingsFeature } from '../../../../../../../store/features/user-settings/user-settings-feature';
import { Store } from '@ngrx/store';
import { MediaDto, MediaWithMeta } from '../../../../../../../../models/ts/media-dto.model';
import {
  FileChunkUpload,
  FileUpload,
  FileUploadService,
  FormDataWithMeta
} from '../../../../../../../core/services/file-upload/file-upload.service';
import { groupBy, GroupResult } from '@progress/kendo-data-query';
import { CellClickEvent, GridModule, GroupModule } from '@progress/kendo-angular-grid';
import { MediaCommitDto } from '../../../../../../../../models/ts/media-commit-dto.model';
import { FileSelectMultiComponent } from '../../../../../../../shared/components/file-select-multi/file-select-multi.component';
import { DocumentEventsService } from 'src/app/shared/services/document-events/document-events.service';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormsModule } from '@angular/forms';
import { CollectionFormApiService } from 'src/app/api/bizzmine/collection-form/collection-form-api.service';
import { DownloadService } from 'src/app/core/services/download/download.service';
import { CollectionFormFileUploadApiService } from 'src/app/api/bizzmine/collection-form-file-upload/collection-form-file-upload-api.service';
import { saveAs } from '@progress/kendo-file-saver';

@Component({
  selector: 'bizz-mailing-attachments-upload',
  standalone: true,
  imports: [
    DragFileUploadDirective,
    IconComponent,
    TranslatePipe,
    AsyncPipe,
    ExtensionIconComponent,
    NgStyle,
    PopoverModule,
    PopupModule,
    DatePipe,
    GridModule,
    GroupModule,
    FileSelectMultiComponent,
    NgClass,
    FormsModule
  ],
  templateUrl: './mailing-attachments-upload.component.html',
  styleUrl: './mailing-attachments-upload.component.scss'
})
export class MailingAttachmentsUploadComponent implements OnInit {
  @Input() public mode: 'mailing' | 'checklists' = 'mailing';
  @Input() public readonly: boolean;
  public isHoveringDragDrop = false;
  public fileUploads: Array<FileUpload> = [];
  @Input() public mediaId: Array<number> = [];
  @Input() public currentAttachments: Array<{
    id: number,
    fileName: string,
    size: number
  }> = [];
  @Output() public attachmentsChanged = new EventEmitter<Array<number>>();
  @Output() public fullAttachmentsChanged = new EventEmitter<Array<{
    id: number,
    fileName: string,
    size: number
  }>>();
  @Output() public downloadAttachmentChanged = new EventEmitter<{id: number,
    fileName: string,
    size: number}>
  public attachments: Array<{
    id: number,
    fileName: string,
    size: number
  }> = [];
  public sizeError = false;
  public emptyExtensionError = false;
  public dateTimeFormatType: string;
  public showPopup = false;
  public gridView: GroupResult[] | MediaDto[];
  public groups = [{ field: 'CollectionName' }];
  public attachmentWidth: number | string = 0;
  @ViewChild('attachmentSelector', { static: true }) public attachmentSelector: ElementRef;
  @ViewChild('MailingAttachmentsUploadComponentFileSelect', { static: true }) public fileSelectMultiComponent: FileSelectMultiComponent;
  public readonly MAX_MOBILE_WIDTH = 1024;
  @ViewChild('popup', { read: ElementRef }) public popup: ElementRef;
  @ViewChild('anchorParent', { read: ElementRef }) public anchorParent: ElementRef;
  private mediaId$ = new BehaviorSubject<Array<number>>([]);
  public media$ = this.mediaId$
    .pipe(
      filter(mediaId => mediaId.length > 0),
      switchMap(mediaId => this.mediaApi.getMediaWithMeta({ mediaIds: mediaId })),
      map(found => {
        found.forEach(media => {
          media.FileSize = this.formatFileSize(media.Size);
        });
        return found;
      }),
      tap(mediaList => {
        //console.debug(mediaList);
        this.gridView = groupBy(mediaList, this.groups);
      }),
      shareReplay(1)
    );
  private MAX_ATTACHMENT_FILE_SIZE_LIMIT = 23000000;
  private chunkSize = 1024 * 1024;
  private currentTotalSize = 0;
  private uploadMeta: Array<{
    fileId: string,
    size: number
  }> = [];


  public constructor(private mediaApi: MediaApiService,
                     private store$: Store,
                     private fileUploadService: FileUploadService,
                     private documentEventsService: DocumentEventsService,
                     private collectionFormFileUploadApiService: CollectionFormFileUploadApiService,
                     private downloadService: DownloadService,
                     private ngZone: NgZone,
                     private destroyRef: DestroyRef) {
    this.dateTimeFormatType = this.store$.selectSignal(
      userSettingsFeature.selectDateTimeFormatTypeString
    )();
  }

  public ngOnInit(): void {
    this.mediaId$.next(this.mediaId);
    if (this.currentAttachments && this.currentAttachments.length > 0) {
      this.attachments = this.attachments.concat(this.currentAttachments);
    }
    asyncScheduler.schedule(() => {
      this.attachmentWidth = this.attachmentSelector.nativeElement.clientWidth + 'px';
    });

    this.documentEventsService.documentClickEvent.pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(
        (event: Event) => {
          if (event?.target && !this.checkHandleClick(event.target)) {
            this.ngZone.run(() => {
              this.onDocumentClick();
            });
          }
        });
    this.documentEventsService.windowResizeEvent.pipe(
      takeUntilDestroyed(this.destroyRef),
      debounceTime(300))
      .subscribe(() => {
        this.attachmentWidth = this.attachmentSelector.nativeElement.clientWidth + 'px';
      });
  }

  public togglePopup(media: MediaWithMeta[]): void {
    this.showPopup = !this.showPopup;
    if (this.showPopup)
      this.syncSelectedVersions(media);
  }

  public setVersion(media: MediaWithMeta, version: 'original' | 'pdf', event: Event): void {
    media.SelectedVersion = media.SelectedVersion == version ? undefined : version;
  }

  public onDocumentClick(): void {
    this.showPopup = false;
  }

  public getFileExtension(fileName: string): string {
    return this.fileUploadService.getFileExtension(fileName);
  }

  public onCellClick(event: CellClickEvent): void {
    this.setVersion(event.dataItem, 'original', event.originalEvent);
  }

  public applySelection(medias: Array<MediaWithMeta>): void {
    this.sizeError = false;
    let selectedMedia = medias.filter(media => media.SelectedVersion != undefined);
    selectedMedia.forEach(media => {
      // Get correct media id
      const mediaId = media.SelectedVersion == 'pdf' && media.PdfId != undefined ? media.PdfId : media.ID;
      // Check for existing ID to replace
      const found = this.attachments.find(_ => _.id == mediaId);

      // Remove alternate (original/pdf) version if already applied
      if (media.PdfId != undefined) {
        const foundAlt = this.attachments.find(_ => _.id == (media.SelectedVersion == 'original' ? media.PdfId : media.ID));
        if (foundAlt != undefined) {
          this.currentTotalSize = this.currentTotalSize - foundAlt.size;
          this.attachments = this.attachments.filter(_ => _.id != foundAlt?.id);
        }
      }

      if (found) {
        this.currentTotalSize = this.currentTotalSize - found.size;
      }
      this.attachments = this.attachments.filter(_ => _.id != mediaId);

      // Check upload size limit
      if (media.Size + this.currentTotalSize < this.MAX_ATTACHMENT_FILE_SIZE_LIMIT) {
        this.currentTotalSize = this.currentTotalSize + media.Size;
        this.attachments.push({
          id: mediaId,
          fileName: media.SelectedVersion == 'pdf' ? this.swapFileExtensionToPdf(media) : media.OriginalFileName,
          size: media.Size
        });
      } else {
        this.sizeError = true;
        setTimeout(() => this.sizeError = false, 15000);
      }
    });
    this.attachmentsChanged.emit(this.attachments.map(attachment => attachment.id));
    this.fullAttachmentsChanged.emit(this.attachments);
    this.showPopup = false;
  }

  public formatFileSize(fileSizeInBytes: number): string {
    return this.fileUploadService.formatFileSize(fileSizeInBytes);
  }

  public openFileSelect(): void {
    this.fileSelectMultiComponent.openFileSelect();
  }

  public validateFile(formData: FormDataWithMeta): Observable<FormDataWithMeta> {
    if (this.uploadMeta.find(meta => meta.fileId == formData.fileId) == null) {
      this.uploadMeta.push({ fileId: formData.fileId, size: formData.meta.size });
    }
    this.sizeError = false;
    this.emptyExtensionError = false;
    this.currentTotalSize = this.currentTotalSize + formData.meta.size;
    if (this.currentTotalSize > this.MAX_ATTACHMENT_FILE_SIZE_LIMIT) {
      this.sizeError = true;
      setTimeout(() => this.sizeError = false, 15000);
      throw new Error('De bestandsgrootte(s) overschrijden de maximumgrootte van 23mb.');
    }
    if (this.getFileExtension(formData.meta.fileName).trim() == '') {
      this.emptyExtensionError = true;
      setTimeout(() => this.emptyExtensionError = false, 15000);
      throw new Error('Het bestand heeft een ongeldige extensie.');
    }

    return of(formData);
  }

  public filesDropped(files: Array<File>): void {
    const fileUploads = this.fileUploadService.uploadFiles({
      upload$: (formData) => this.mediaApi.uploadFileFromFormData(formData),
      files: files,
      chunkSize: this.chunkSize,
      maxConcurrentUploads: null,
      preValidation$: (data) => this.validateFile(data)
    });

    fileUploads.forEach(fileUpload => {
      fileUpload.onError = (): void => {
        console.error('onError', fileUpload);
        this.cancelUpload(fileUpload.fileId);
      };
      fileUpload.onCompleted = (): void => {
        if (fileUpload.fileChunkUpload) {
          const mediaCommitDto: MediaCommitDto = {
            FileName: fileUpload.fileChunkUpload.fileName,
            LastModifiedDate: fileUpload.fileChunkUpload.lastModifiedDate.toString(),
            Identifier: fileUpload.fileId,
            Type: fileUpload.fileChunkUpload.type,
            Size: fileUpload.fileChunkUpload.size,
            ChunkIDs: fileUpload.fileChunkUpload.chunkIds,
            MediasID: 0,
            CollectionsID: 0,
            ContentType: '',
            MimeValidation: '',
            VersionsID: 0
          };
          lastValueFrom(this.mediaApi.commitMailAttachment(mediaCommitDto))
            .then((response) => {
              this.finishUpload(fileUpload.fileId, response, fileUpload.fileChunkUpload as FileChunkUpload);
            });
        }
      };
    });

    this.fileUploads = this.fileUploads.concat(fileUploads);
  }

  public finishUpload(fileId: string, id: number, item: FileChunkUpload): void {
    this.fileUploads = this.fileUploads.filter(file => file.fileId != fileId);
    this.attachments.push({
      id: id,
      size: item.size,
      fileName: item.fileName
    });
    this.attachmentsChanged.emit(this.attachments.map(attachment => attachment.id));
    this.fullAttachmentsChanged.emit(this.attachments);
  }

  public removeAttachment(id: number): void {
    const found = this.attachments.find(attachment => attachment.id == id);
    if (found) this.currentTotalSize = this.currentTotalSize - found.size;
    this.attachments = this.attachments.filter(attachment => attachment.id != id);
    this.attachmentsChanged.emit(this.attachments.map(attachment => attachment.id));
    this.fullAttachmentsChanged.emit(this.attachments);
  }

  public cancelUpload(fileId: string): void {
    const foundMeta = this.uploadMeta.find(meta => meta.fileId == fileId);
    if (foundMeta) this.currentTotalSize = this.currentTotalSize - foundMeta.size;
    this.uploadMeta = this.uploadMeta.filter(meta => meta.fileId != fileId);
    this.fileUploads = this.fileUploads.filter(fileUpload => fileUpload.fileId != fileId);
  }

  private syncSelectedVersions(media: MediaWithMeta[]): void {
    media = media.map(m => {
      const existingAttachment = this.attachments.find(a => a.id == m.PdfId || a.id == m.ID);
      if (existingAttachment) {
        m.SelectedVersion = m.ID == existingAttachment.id ? 'original' : 'pdf';
      } else m.SelectedVersion = undefined;
      return m;
    });
  }

  private checkHandleClick(target: EventTarget): boolean {
    return (
      (this.attachmentSelector ? this.attachmentSelector.nativeElement.contains(target) : false) ||
      (this.anchorParent ? this.anchorParent.nativeElement.contains(target) : false) ||
      (this.popup ? this.popup.nativeElement.contains(target) : false)
    );
  }

  /**
   * Returns media.OriginalFileName's extension with .pdf provided it contains an extension and is not already a pdf.
   * @param {MediaWithMeta} media
   * @private
   */
  private swapFileExtensionToPdf(media: MediaWithMeta): string {
    if (this.fileUploadService.getFileExtension(media.OriginalFileName) != 'pdf') {
      let splitStr: string[] = media.OriginalFileName.split('.');
      // More than one element means there is a file ext at the end
      if (splitStr.length > 1) {
        // replace the last element with pdf
        splitStr[splitStr.length - 1] = '.pdf';
        // rejoin the string
        return splitStr.join('.');
      }
    }
    return media.OriginalFileName;
  }

  public downloadAttachment(attachment: {
    id: number,
    fileName: string,
    size: number
  }): void {
    //Ensure the attachment already exists and wasn’t just uploaded
    //if (this.mediaId.includes(attachment.id)){
      this.downloadAttachmentChanged.emit(attachment);
    //}
  }

}
